import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Columns2, Eye, Lightbulb, PenLine } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../use-debounced-value';
import { useWindowKeydown } from '../use-window-keydown';
import { useOverlays } from '../work/overlay-context';
import { ApiError, apiErrorCode, apiErrorMessage } from '../../lib/api';
import { uploadPendingAttachment } from '../../lib/attachments';
import { formatTimestampDateTime } from '../../lib/dates';
import {
  clearWikiDraft,
  loadWikiDraft,
  patchWikiPage,
  saveWikiDraft,
  wikiRoute,
  type WikiPageFull,
  type WikiRevisionFull,
  type WikiSpaceRef,
} from '../../lib/wiki';
import MarkdownSourceEditor from './MarkdownSourceEditor';
import WikiMarkdownView from './WikiMarkdownView';
import { useWikiEditorMode, type WikiEditorMode } from './use-wiki-editor-mode';

type Props = {
  space: WikiSpaceRef;
  spaceName: string;
  page: WikiPageFull;
  /** Revision to pre-fill (History › Restore into editor). */
  restore: WikiRevisionFull | null;
  onSaved: (page: WikiPageFull) => void;
  onClose: () => void;
  confirm: (opts: { title: string; description: string; confirmLabel?: string; destructive?: boolean }) => Promise<boolean>;
  toast: (message: string) => void;
};

type Conflict = { currentVersion: number | null };

const DRAFT_DEBOUNCE_MS = 800;
const PREVIEW_DEBOUNCE_MS = 250;
/** Height of the app header; the editor fills the rest of the viewport. */
const CANVAS_MIN_HEIGHT = 'min-h-[calc(100dvh-3rem)]';

const MODES: Array<{ value: WikiEditorMode; label: string; Icon: typeof PenLine }> = [
  { value: 'source', label: 'Source', Icon: PenLine },
  { value: 'split', label: 'Split', Icon: Columns2 },
  { value: 'preview', label: 'Preview', Icon: Eye },
];

function currentVersionFromError(err: ApiError): number | null {
  const details = err.body.error?.details;
  if (!Array.isArray(details)) return null;
  for (const detail of details) {
    const value = (detail as { current_version?: unknown }).current_version;
    if (typeof value === 'number') return value;
  }
  return null;
}

/** Screens 4 and 5: explicit Update, local draft, conflict banner. */
export default function WikiPageEditor({ space, spaceName, page, restore, onSaved, onClose, confirm, toast }: Props) {
  const draft = restore ? null : loadWikiDraft(page.id, page.version);
  const [title, setTitle] = useState(restore?.title ?? draft?.title ?? page.title);
  const [body, setBody] = useState(restore?.body_md ?? draft?.body_md ?? page.body_md);
  const [mode, setMode] = useWikiEditorMode();
  const previewBody = useDebouncedValue(body, PREVIEW_DEBOUNCE_MS);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(draft?.saved_at ?? null);
  const [restoredFrom] = useState<string | null>(draft && !restore ? draft.saved_at : null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const { isBlocked } = useOverlays();

  // Source editing stores exactly what is typed, so the raw Markdown is the baseline.
  const dirty = title !== page.title || body !== page.body_md;
  const nextMode = MODES[(MODES.findIndex((row) => row.value === mode) + 1) % MODES.length].value;

  useEffect(() => {
    if (!dirty) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const saved = saveWikiDraft(page.id, page.version, { title, body_md: body });
      setDraftSavedAt(saved.saved_at);
    }, DRAFT_DEBOUNCE_MS);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [title, body, dirty, page.id, page.version]);

  const upload = useCallback(async (file: File) => {
    const dto = await uploadPendingAttachment(file);
    return dto.download_url;
  }, []);

  async function update() {
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setConflict(null);
    try {
      const patch: { version: number; title?: string; body_md?: string } = { version: page.version };
      if (trimmed !== page.title) patch.title = trimmed;
      if (body !== page.body_md) patch.body_md = body.trimEnd();
      const saved = Object.keys(patch).length === 1 ? page : await patchWikiPage(space, page.id, patch);
      clearWikiDraft(page.id, page.version);
      toast(saved === page ? 'No changes' : 'Page updated');
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && apiErrorCode(err) === 'STALE_VERSION') {
        setConflict({ currentVersion: currentVersionFromError(err) });
      } else {
        setError(apiErrorMessage(err, 'Failed to update page.'));
      }
    } finally {
      setSaving(false);
    }
  }

  async function close() {
    if (dirty) {
      const ok = await confirm({
        title: 'Discard changes?',
        description: 'Your edits are only saved in this browser until you press Update.',
        confirmLabel: 'Discard',
        destructive: true,
      });
      if (!ok) return;
    }
    clearWikiDraft(page.id, page.version);
    onClose();
  }

  useWindowKeydown((event) => {
    // Something closer to the key (CodeMirror, a dialog) already consumed it.
    if (event.defaultPrevented || event.altKey) return;
    const meta = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();
    if (meta && !event.shiftKey && key === 's') {
      // Always swallow ⌘S so the browser's Save dialog never opens while editing.
      event.preventDefault();
      if (!saving && !conflict && !isBlocked) void update();
      return;
    }
    if (meta && event.shiftKey && key === 'e') {
      event.preventDefault();
      setMode(nextMode);
      return;
    }
    if (!meta && !event.shiftKey && event.key === 'Escape' && !isBlocked && !saving) {
      event.preventDefault();
      void close();
    }
  });

  async function copyChanges() {
    try {
      await navigator.clipboard.writeText(`# ${title}\n\n${body}`);
      toast('Copied your changes');
    } catch (err) {
      toast(apiErrorMessage(err, 'Clipboard is not available'));
    }
  }

  const header = (
    <div className="flex items-center justify-between gap-3 px-4 py-2">
      <nav aria-label="Breadcrumb" className="min-w-0 truncate text-xs text-muted-foreground">
        <Link to={wikiRoute(space)} className="hover:underline">
          {spaceName}
        </Link>
        {page.ancestors.map((ancestor) => (
          <span key={ancestor.id}> › {ancestor.title}</span>
        ))}
        <span> › {page.title}</span>
      </nav>
      <div className="flex shrink-0 items-center gap-3">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={mode}
          onValueChange={(next) => {
            // Radix reports '' when the active item is clicked again; the mode stays as it is.
            if (next === 'source' || next === 'split' || next === 'preview') setMode(next);
          }}
          aria-label="Editor layout"
          className="gap-0 -space-x-px"
        >
          {MODES.map(({ value, label, Icon }) => (
            <Tooltip key={value}>
              <TooltipTrigger asChild>
                <ToggleGroupItem
                  value={value}
                  aria-label={label}
                  className="h-8 w-8 rounded-none p-0 first:rounded-l-md last:rounded-r-md"
                >
                  <Icon className="h-4 w-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>{label} · ⌘⇧E cycles</TooltipContent>
            </Tooltip>
          ))}
        </ToggleGroup>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          Editing v{page.version}
          {draftSavedAt ? ` · draft saved ${formatTimestampDateTime(draftSavedAt) ?? ''}` : ''}
        </span>
        <Button type="button" variant="outline" size="sm" title="Close (Esc)" onClick={() => void close()} disabled={saving}>
          Close
        </Button>
        <Button
          type="button"
          size="sm"
          title="Update (⌘S / Ctrl+S)"
          onClick={() => void update()}
          disabled={saving || Boolean(conflict)}
        >
          {saving ? 'Updating…' : 'Update'}
        </Button>
      </div>
    </div>
  );

  const lead = (
    <div className="mb-4 space-y-3">
      {conflict ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>This page changed while you were editing</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              {conflict.currentVersion !== null
                ? `The server has v${conflict.currentVersion}; you are editing v${page.version}.`
                : `You are editing v${page.version}, which is no longer current.`}{' '}
              Copy your changes, reload, and apply them again.
            </p>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => void copyChanges()}>
                Copy my changes
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {restoredFrom ? (
        <p className="text-xs text-muted-foreground">
          Draft restored from {formatTimestampDateTime(restoredFrom) ?? restoredFrom}.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              clearWikiDraft(page.id, page.version);
              setTitle(page.title);
              setBody(page.body_md);
              setDraftSavedAt(null);
            }}
          >
            Discard draft
          </button>
        </p>
      ) : null}
      {restore ? (
        <p className="text-xs text-muted-foreground">Editing content from v{restore.version}. Nothing changes until Update.</p>
      ) : null}

      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={255}
        aria-label="Page title"
        placeholder="Give this page a title"
        className="h-auto rounded-none border-0 bg-transparent px-0 py-1 text-[32px] font-bold leading-tight tracking-tight shadow-none focus-visible:ring-0 md:text-[32px]"
      />
    </div>
  );

  const source = (
    <MarkdownSourceEditor
      value={body}
      onChange={setBody}
      upload={upload}
      onUploadError={toast}
      placeholder="Write Markdown here."
      className="min-h-[50vh]"
    />
  );
  const preview = <WikiMarkdownView value={previewBody} />;

  // Negative margin cancels AppLayout's page inset so the editor fills the main area.
  return (
    <div className={cn('-m-4 flex flex-col bg-background', CANVAS_MIN_HEIGHT)}>
      <div className="sticky top-0 z-20 border-b bg-background">{header}</div>
      <div className={cn('mx-auto w-full flex-1 px-6 pb-32 pt-10 sm:px-10', mode === 'split' ? 'max-w-7xl' : 'max-w-3xl')}>
        {lead}
        {mode === 'source' ? source : null}
        {mode === 'preview' ? preview : null}
        {mode === 'split' ? (
          <div className="grid gap-8 md:grid-cols-2 md:divide-x">
            <div className="min-w-0">{source}</div>
            <div className="min-w-0 md:pl-8">{preview}</div>
          </div>
        ) : null}
        {mode === 'preview' ? null : (
          <Alert className="mt-6 bg-muted/40">
            <Lightbulb className="h-4 w-4" />
            <AlertTitle>Tip</AlertTitle>
            <AlertDescription className="text-muted-foreground">
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>
                  A <code>[KEY](/browse/KEY)</code> link on its own line embeds the work item.
                </li>
                <li>
                  A <code>```mermaid</code> fence renders a diagram.
                </li>
                <li>Paste or drop images to upload them.</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
